import { Badge } from '@/components/ui/badge';
import { highlightMatch } from '@/lib/highlight-match';
import { getCategoryStyle } from '@/lib/instrument-categories';
import { parseInstrumentName, splitVariant } from '@/lib/instrument-name';
import type { Test } from '@/api/tests';
import type { ReactNode } from 'react';

interface InstrumentPickerRowProps {
    test: Test;
    /** Highlighted inside the name, so a match is visible without reading. */
    query: string;
    /** Whether another instrument in the list resolves to the same acronym. */
    showVariant: boolean;
    /** Assignment state, when the patient already has one. */
    badge?: ReactNode;
    action?: ReactNode;
}

/**
 * One instrument in the patient assignment picker.
 *
 * Carries the acronym, the name and the category, and nothing else. Item count,
 * duration, audience and the description all live on `/assets`, where an
 * instrument is being studied; here the psychologist already knows what they
 * want and is looking it up among forty, so every extra fact is a row they have
 * to read past. The acronym leads because it is what they search by.
 */
export default function InstrumentPickerRow({
    test,
    query,
    showVariant,
    badge,
    action,
}: InstrumentPickerRowProps) {
    const { acronym, shortName } = parseInstrumentName(test.name, test.test_id);
    const { base, variant } = splitVariant(shortName);
    const categoryStyle = getCategoryStyle(test.category);

    return (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card py-2 pl-3.5 pr-2.5 transition-colors hover:border-border-strong">
            <div className="min-w-0 flex-1">
                {/* Wraps rather than truncates: a clipped name ending in an
                    ellipsis is one the reader has to open to identify, and half
                    the catalog runs past a single line at this width. */}
                <p className="text-pretty text-sm leading-5">
                    <span className="font-bold text-foreground">
                        {highlightMatch(acronym, query)}
                    </span>
                    {showVariant && variant ? (
                        <span className="font-medium text-muted-foreground">
                            {' ('}
                            {highlightMatch(variant, query)}
                            {')'}
                        </span>
                    ) : null}
                    <span className="text-muted-foreground"> · {highlightMatch(base, query)}</span>
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {test.category ? (
                        <Badge variant="outline" className={categoryStyle.badgeClassName}>
                            {highlightMatch(test.category, query)}
                        </Badge>
                    ) : null}
                    {badge}
                </div>
            </div>
            {action}
        </div>
    );
}

export function InstrumentPickerRowSkeleton() {
    return (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card py-2 pl-3.5 pr-2.5">
            <div className="min-w-0 flex-1 space-y-2">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-4 w-20 animate-pulse rounded bg-muted" />
            </div>
            <div className="h-8 w-20 animate-pulse rounded-md bg-muted" />
        </div>
    );
}
